import { LabeledAddressInput, LabeledSearchSelect, useLocalization } from '@magicdoorinc/ui';
import { createEffect, createSignal, on } from 'solid-js';
import { fetchPlaceAddress, fetchPlacePredictions } from '~/utils/googlePlaces';
import type { IAddress, LabeledAddressInputProps } from '@magicdoorinc/ui';
import type { Component } from 'solid-js';
import type { PlacePrediction } from '~/utils/googlePlaces';

const isPlacePrediction = (item: unknown): item is PlacePrediction => {
  return !!item && typeof item === 'object' && 'id' in item && typeof (item as PlacePrediction).id === 'string';
};

const isSameAppliedAddress = (value: IAddress | undefined, applied: IAddress | undefined) => {
  if (!applied) {
    return false;
  }
  return (
    value?.streetAddress1 === applied.streetAddress1 &&
    value?.streetAddress2 === applied.streetAddress2 &&
    value?.city === applied.city &&
    value?.state === applied.state &&
    value?.zipCode === applied.zipCode &&
    value?.country === applied.country
  );
};

const fetchPlaces = async (query: string | undefined, _page: number) => {
  const items = await fetchPlacePredictions(query);
  return { items, totalPages: 1 };
};

export const LabeledGoogleAddressInput: Component<LabeledAddressInputProps> = (props) => {
  const { t } = useLocalization();
  const [selectedPlace, setSelectedPlace] = createSignal<PlacePrediction | undefined>();
  const [appliedAddress, setAppliedAddress] = createSignal<IAddress | undefined>();

  const clearSelection = () => {
    setSelectedPlace(undefined);
    setAppliedAddress(undefined);
  };

  createEffect(
    on(
      () => props.value,
      (value) => {
        if (!selectedPlace()) {
          return;
        }
        if (!isSameAppliedAddress(value, appliedAddress())) {
          clearSelection();
        }
      },
      { defer: true }
    )
  );

  const handlePlaceSelect = async (item: PlacePrediction | PlacePrediction[] | undefined) => {
    const place = Array.isArray(item) ? item[0] : item;
    if (!isPlacePrediction(place)) {
      clearSelection();
      return;
    }

    setSelectedPlace(place);

    const details = await fetchPlaceAddress(place.id);
    if (!details) {
      clearSelection();
      return;
    }

    setAppliedAddress(details);
    props.onInput?.({
      ...props.value,
      ...details,
    });
  };

  return (
    <div class="flex flex-col gap-4">
      <LabeledSearchSelect
        label={t('Search address')}
        placeholder={t('Start typing an address')}
        multiple={false}
        disabled={props.disabled}
        selected={selectedPlace()}
        onSelect={handlePlaceSelect}
        renderItem={(item: PlacePrediction) => (
          <span>
            <span class="text-[#111827]">{item.main_text || item.description}</span>
            <span class="ml-1 text-[#718096]">{item.secondary_text}</span>
          </span>
        )}
        renderSelected={(item: PlacePrediction) => (
          <span>{item.description || [item.main_text, item.secondary_text].filter(Boolean).join(', ')}</span>
        )}
        fetcher={fetchPlaces}
      />
      <LabeledAddressInput {...props} />
    </div>
  );
};
